import { navigate } from 'gatsby'
// contact.tsx
import React, { useMemo } from "react"
import { sendTemplateMail, } from "gatsby-jaen-emailwerk"

import { useLocation } from "@reach/router"
import type { ContactFormValues } from "../components/ContactModal/ContactModal"

/**
 * The dialog is loaded when it is first opened, not when the page is.
 *
 * ContactModalProvider is mounted by gatsby-plugin-jaen's Layout on EVERY
 * route, so a static import puts the whole form in the chunk every visitor
 * downloads before first paint. react-hook-form alone is 133 KB of source
 * there, for a dialog most visitors never open.
 *
 * A modal is the safe place to do this, unlike an inline editor: it is an
 * overlay that appears after a click, so the Suspense gap costs no layout
 * shift, and the reader is already waiting for a dialog to appear. `ContactFormValues`
 * comes across as a type-only import, which carries no runtime edge.
 */
const ContactModal = React.lazy(() =>
  import("../components/ContactModal/ContactModal").then(m => ({
    default: m.ContactModal
  }))
)
import { useAuth, useNotificationsContext } from "jaen"
import { useIntl } from "react-intl"
import { useQueryRouter } from "../hooks/use-query-router"

export interface ContactModalContextProps {

  onOpen: (args?: {
    meta?: Record<string, any>
    /** Prefills the message field, e.g. the booking summary the Termin
     *  section composes. A visitor can still edit it before sending. */
    message?: string
    /** Where to navigate after a successful send. The /termin landing page
     *  passes its confirmation page here so an ads conversion has a URL to
     *  fire on; everywhere else the toast is confirmation enough. */
    onSuccessNavigateTo?: string
  }) => void
  onClose: () => void
}

export const ContactModalContext =
  React.createContext<ContactModalContextProps>({
    onOpen: () => {},
    onClose: () => {},
  })

export const useContactModal = () => {
  const context = React.useContext(ContactModalContext)
  if (!context) {
    throw new Error("useContactModal must be used within a ContactModalProvider")
  }
  return context
}

export interface ContactModalDrawerProps {
  children: React.ReactNode
}

export const ContactModalProvider: React.FC<ContactModalDrawerProps> = ({ children }) => {
  // Use the current location from @reach/router.
  const location = useLocation()
  const { isCalled, paramValue } = useQueryRouter(location, "contact")

  const [meta, setMeta] = React.useState<Record<string, any> | null>(null)
  const [isOpen, setIsOpen] = React.useState(false)

  // v3 dropped useToast for a store whose create() reads `type`, not `status`.
  // jaen's notifications context wraps that store back up in v2's option shape
  // and is already mounted above every page, so the two calls below keep the
  // placement, timing and close button they had.
  const { toast } = useNotificationsContext()
  const authentication = useAuth()
  const intl = useIntl()

  // Helper: always get a full URL (incl. protocol, domain, path, search, hash) and guard SSR
  const getCurrentUrl = React.useCallback(() => {
    if (typeof window !== "undefined" && window.location) {
      // Prefer the full href; this includes search and hash even on root "/"
      return window.location.href
    }
    // SSR-safe fallback using @reach/router location
    const pathname = location?.pathname ?? "/"
    const search = location?.search ?? ""
    const hash = location?.hash ?? ""
    return `${pathname}${search}${hash}`
  }, [location])

  // When the query parameter is present, open the modal AND set meta.url
  React.useEffect(() => {
    if (isCalled) {
      setMeta(prev => ({ ...prev, url: getCurrentUrl() }))
      setIsOpen(true)
    }
  }, [isCalled, getCurrentUrl])

  const [prefillMessage, setPrefillMessage] = React.useState<string | undefined>(
    undefined,
  )
  const [successPath, setSuccessPath] = React.useState<string | undefined>(
    undefined,
  )

  const onOpen: ContactModalContextProps["onOpen"] = (args) => {
    const updatedMeta = {
      ...meta,
      url: getCurrentUrl(), // ensure we always store a URL when opening
      ...args?.meta,
    }
    setMeta(updatedMeta)
    setPrefillMessage(args?.message)
    setSuccessPath(args?.onSuccessNavigateTo)
    setIsOpen(true)
  }

  const onClose = () => {
    // Remove the "contact" query parameter from the URL without reloading the page.
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href)
      url.searchParams.delete("contact")
      window.history.replaceState({}, "", url.toString())
    }
    setIsOpen(false)
  }

  const onSubmit = async (data: ContactFormValues): Promise<void> => {
    // Never empty: prefer meta.url, else current url, else "unknown"
    const invokedOnUrl = meta?.url ?? getCurrentUrl() ?? "unknown"

    // The "Contact" template on emailwerk.com. It was migrated out of
    // mailpress with the other 29 in one bulk import, which minted new cuids,
    // and only this call site was left pointing at the old mailpress UUID.
    //
    // One send, for visitors and logged-in users alike: the template is
    // flagged isPublic, so this posts anonymously when there is no CMS session
    // and emailwerk takes the public branch. Recipients come from the stored
    // envelope (office@nadine-hauswirth.com) server-side and cannot be passed
    // from here at all; the requester's own address goes into replyTo, which is
    // also where the linked "Contact Confirmation" is delivered.
    const result = await sendTemplateMail(
      "cmsmguth4001erb2pgbodg9rs",
      {
        envelope: {
          replyTo: data.email,
        },
        values: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone || "",
          message: data.message,
          invokedOnUrl, // <-- full href incl. search + hash; never empty
          // Only the page's language goes along for the confirmation mail.
          // Its subject and texts are fixed in the "Contact Confirmation"
          // template, one set per language of the site, chosen by this value
          // and German when it is missing or unknown.
          locale: intl.locale,
        },
      }
    )

    if (!result.ok) {
      toast({
        title: intl.formatMessage({
          id: "ContactToastErrorTitle",
          defaultMessage: "Fehler",
        }),
        description: intl.formatMessage({
          id: "ContactToastErrorDescription",
          defaultMessage: "Es ist ein Fehler aufgetreten.",
        }),
        status: "error",
        duration: 5000,
        isClosable: true,
      })
    } else {
      toast({
        title: intl.formatMessage({
          id: "ContactToastSuccessTitle",
          defaultMessage: "Erfolg",
        }),
        description: intl.formatMessage({
          id: "ContactToastSuccessDescription",
          defaultMessage: "Ihre Nachricht wurde erfolgreich versendet.",
        }),
        status: "success",
        duration: 5000,
        isClosable: true,
      })
      onClose()
      if (successPath) {
        void navigate(successPath)
      }
    }
  }

  const fixedValues = useMemo(() => {
    if (!authentication.user) {
      return undefined
    }
    return {
      firstName: authentication.user.profile?.given_name,
      lastName: authentication.user.profile?.family_name,
      email: authentication.user.profile?.email,
    }
  }, [authentication.user])

  const defaultValues = useMemo(() => {
    if (prefillMessage) {
      return { message: prefillMessage }
    }
    if (!isCalled) {
      return undefined
    }
    return {
      message: paramValue,
    }
  }, [prefillMessage, isCalled, paramValue])

  return (
    <ContactModalContext.Provider value={{ onOpen, onClose }}>
      {children}
      {/* Mounted only once opened. Rendering it closed would defeat the lazy
          import, because Suspense resolves the chunk as soon as the element
          exists. */}
      {isOpen && (
        <React.Suspense fallback={null}>
          <ContactModal
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={onSubmit}
            fixedValues={fixedValues}
            defaultValues={defaultValues}
          />
        </React.Suspense>
      )}
    </ContactModalContext.Provider>
  )
}
